import React, { useState } from 'react';
import styled from 'styled-components';
import { theme, media } from './theme';
import { useTranslation, Trans } from 'react-i18next';
import Header from './components/Header';
import { FAQ_ITEMS } from './config/faq';
import bgImage from './assets/graphics/bg.jpg';

// Styled components
const PageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: calc(100svh - ${theme.spacing.controlBarHeight}px);
  display: flex;
  flex-direction: column;
  color: ${theme.colors.gold};
  box-sizing: border-box;
  padding: 60px 50px ${theme.spacing.lg} 50px;
  background-image: url(${bgImage});
  background-size: cover;
  background-position: center;
  overflow-y: auto;
  overflow-x: hidden;

  ${media.tablet} {
    padding: ${theme.spacing.xl} ${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.lg};
  }
`;

const Container = styled.div`
  flex: 1;
  color: ${theme.colors.white};
  padding: 2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;

  ${media.tablet} {
    padding: ${theme.spacing.lg};
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
  flex-grow: 1;
  height: 100%;

  ${media.desktopLg} {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: start;
  padding-right: ${theme.spacing.xl};
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: ${theme.spacing.xl};
`;

const BackButton = styled.button`
  background: ${theme.colors.goldRadialAlpha15};
  border: 1px solid ${theme.colors.gold};
  color: ${theme.colors.gold};
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  font-size: ${theme.fontSizes.md};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: background ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.goldRadialAlpha30};
  }
`;

const FAQButton = styled.button`
  width: 100%;
  background: ${theme.colors.overlayDark};
  border: 1px solid ${theme.colors.gold};
  color: ${theme.colors.gold};
  padding: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.md};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: background ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.goldDark};
  }
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes.huge};
  margin: 0 0 1.5rem;
  display: flex;
  align-items: center;
  color: ${theme.colors.gold};

  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 2rem;
    background: ${theme.colors.gold};
    margin-right: 0.5rem;
  }
`;

const QuestionColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem 2.5rem;

  ${media.tablet} {
    grid-template-columns: 1fr;
  }
`;

const Question = styled.div`
  color: ${theme.colors.gray300};
  cursor: pointer;
  transition: color ${theme.transitions.normal};
  font-size: ${theme.fontSizes.md};

  &:hover {
    color: ${theme.colors.gold};
  }
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${theme.colors.overlayDarker};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${theme.zIndex.modal};
`;

const Popup = styled.div`
  background: ${theme.colors.bgDark};
  padding: 2rem;
  border-radius: ${theme.borderRadius.lg};
  max-width: 600px;
  width: 90%;
  box-shadow: ${theme.shadows.popup};
  position: relative;
  max-height: 90%;
  overflow-y: auto;
`;

const PopupClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${theme.colors.gray400};
  font-size: 1.5rem;
  cursor: pointer;
  transition: color ${theme.transitions.normal};

  &:hover {
    color: ${theme.colors.white};
  }
`;

const PopupTitle = styled.h3`
  margin-top: 0;
  color: ${theme.colors.gold};
  font-size: 1.75rem;
`;

const PopupContent = styled.div`
  color: #eee;
  line-height: ${theme.lineHeights.relaxed};
  margin-top: 1rem;
`;

/**
 * Props pro QAPage komponentu
 */
interface QAPageProps {
  /** Callback volaný při kliknutí na tlačítko zpět */
  onBack: () => void;
}

/**
 * Komponenta Q&A stránky s FAQ položkami
 */
export default function QAPage({ onBack }: QAPageProps): React.ReactElement {
  const { t } = useTranslation(['faq', 'common']);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  return (
    <PageContainer>
      <Header />

      <Container>
        {/* Hlavní dvousloupcový wrapper */}
        <TopSection>
          <LeftSection>
            <BackButton onClick={onBack}>
              {t('common:buttons.backToForm')}
            </BackButton>
            <Question
              key='17'
              onClick={() => setActiveKey('17')}
            >
              <FAQButton>
                {t('common:buttons.contact')}
              </FAQButton>
            </Question>
          </LeftSection>
          <RightSection>
            <Title>{t('faq:title')}</Title>
            <QuestionColumns>
              {FAQ_ITEMS
                .filter(item => item.inList !== false)
                .map(item => (
                  <Question
                    key={item.key}
                    onClick={() => setActiveKey(item.key)}
                  >
                    {t(`faq:items.${item.key}.question`)}
                  </Question>
                ))}
            </QuestionColumns>
          </RightSection>
        </TopSection>

        {/* Popup */}
        {activeKey && (
          <PopupOverlay onClick={() => setActiveKey(null)}>
            <Popup onClick={e => e.stopPropagation()}>
              <PopupClose onClick={() => setActiveKey(null)}>✕</PopupClose>
              <PopupTitle>
                {t(`faq:items.${activeKey}.question`)}
              </PopupTitle>
              <PopupContent style={{ whiteSpace: 'pre-line' }}>
                <Trans
                  i18nKey={`faq:items.${activeKey}.answer`}
                  components={{
                    strong: <strong />,
                    a: <a href="#" target="_blank" rel="noopener noreferrer" />
                  }}
                />
              </PopupContent>
            </Popup>
          </PopupOverlay>
        )}
      </Container>
    </PageContainer>
  );
}
